import { BriefcaseIcon, CheckIcon, ChevronRightIcon, HistoryIcon, SendIcon, UsersIcon, UsersRoundIcon, type LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { JobMark } from '@/components/JobMark';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useMe } from '@/lib/auth';
import type { MeJob } from '@/lib/types';
import { cn } from '@/lib/utils';

const LAST_JOB_KEY = 'crewline-last-job';

/**
 * App-style bottom navigation for phones: Jobs, Compose, History, Team. History and Team point at the
 * job in the current URL, else the last job visited on this device, else the first active job.
 * Compose asks which job the message is for whenever there is more than one to choose from (a bottom
 * sheet listing the jobs the person can send on, current job first); with a single job it goes
 * straight there. People who cannot send anywhere get Contacts in its place. Hidden from `md` up,
 * where the sidebar takes over.
 */
export function MobileTabBar() {
  const me = useMe();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const inUrl = /^\/jobs\/([0-9a-f-]{36})/i.exec(pathname)?.[1] ?? null;
  const [last, setLast] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LAST_JOB_KEY);
    } catch {
      return null;
    }
  });
  const [picking, setPicking] = useState(false);
  useEffect(() => {
    if (!inUrl) return;
    setLast(inUrl);
    try {
      localStorage.setItem(LAST_JOB_KEY, inUrl);
    } catch {
      /* ignore */
    }
  }, [inUrl]);

  const active = me.jobs.filter((j) => j.status === 'active');
  const jobId = inUrl ?? (last && active.some((j) => j.id === last) ? last : active[0]?.id) ?? null;
  const sendable = active.filter((j) => j.myRole === 'admin' || me.permissions.createJobs);
  // Current job first so the likely answer is under the thumb.
  const choices = [...sendable].sort((a, b) => Number(b.id === jobId) - Number(a.id === jobId));

  const section = (name: string) => new RegExp(`^/jobs/[0-9a-f-]{36}/${name}(/|$)`, 'i').test(pathname);
  const composeActive = section('compose');
  const compose: Item =
    sendable.length === 0
      ? { key: 'contacts', to: `/jobs/${jobId}/contacts`, label: 'Contacts', icon: UsersIcon, active: section('contacts'), disabled: !jobId }
      : sendable.length === 1
        ? { key: 'compose', to: `/jobs/${sendable[0]!.id}/compose`, label: 'Compose', icon: SendIcon, active: composeActive }
        : { key: 'compose', onPress: () => setPicking(true), label: 'Compose', icon: SendIcon, active: composeActive };
  const items: Item[] = [
    compose,
    { key: 'history', to: `/jobs/${jobId}/history`, label: 'History', icon: HistoryIcon, active: section('history'), disabled: !jobId },
    { key: 'team', to: `/jobs/${jobId}/team`, label: 'Team', icon: UsersRoundIcon, active: section('team'), disabled: !jobId },
  ];
  const jobsActive = pathname.startsWith('/jobs') && !items.some((i) => i.active);
  const all: Item[] = [{ key: 'jobs', to: '/jobs', label: 'Jobs', icon: BriefcaseIcon, active: jobsActive }, ...items];

  const choose = (job: MeJob) => {
    setPicking(false);
    void navigate(`/jobs/${job.id}/compose`);
  };

  return (
    <>
      <nav className="bg-background/95 fixed inset-x-0 bottom-0 z-30 border-t backdrop-blur md:hidden" aria-label="Primary">
        <ul className="grid grid-cols-4 pb-[env(safe-area-inset-bottom)]">
          {all.map((item) => (
            <li key={item.key}>
              <TabButton item={item} />
            </li>
          ))}
        </ul>
      </nav>

      <Sheet open={picking} onOpenChange={setPicking}>
        <SheetContent side="bottom" className="gap-0 p-0 pb-[env(safe-area-inset-bottom)] md:hidden" aria-describedby={undefined}>
          <SheetHeader className="px-5 pt-5 pb-3">
            <SheetTitle className="font-display text-xl font-normal">Compose a message</SheetTitle>
            <SheetDescription>Which job is it for?</SheetDescription>
          </SheetHeader>
          <ul className="max-h-[60dvh] overflow-y-auto border-t">
            {choices.map((job) => {
              const current = job.id === jobId;
              return (
                <li key={job.id} className="border-b last:border-b-0">
                  <button type="button" onClick={() => choose(job)} className="hover:bg-muted/60 active:bg-muted flex w-full items-center gap-3 px-5 py-3 text-left transition-colors">
                    <JobMark job={job} size="md" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{job.name}</span>
                      <span className="text-muted-foreground block text-xs">{job.myRole === 'admin' ? 'PM Admin' : 'PM User'}</span>
                    </span>
                    {current ? (
                      <span className="text-primary flex items-center gap-1 text-[11px] font-medium tracking-wide uppercase">
                        <CheckIcon className="size-3.5" aria-hidden /> Current
                      </span>
                    ) : (
                      <ChevronRightIcon className="text-muted-foreground size-4" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>
    </>
  );
}

type Item = { key: string; label: string; icon: LucideIcon; active: boolean; disabled?: boolean } & ({ to: string; onPress?: undefined } | { to?: undefined; onPress: () => void });

function TabButton({ item }: { item: Item }) {
  const className = cn(
    'relative flex h-16 w-full flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors active:scale-95',
    item.active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
    item.disabled && 'pointer-events-none opacity-40',
  );
  const inner = (
    <>
      <span className={cn('bg-primary absolute inset-x-6 top-0 h-0.5 transition-opacity', item.active ? 'opacity-100' : 'opacity-0')} aria-hidden />
      <item.icon className="size-5" strokeWidth={item.active ? 2.25 : 1.75} aria-hidden />
      {item.label}
    </>
  );
  if (item.onPress) {
    return (
      <button type="button" onClick={item.onPress} aria-current={item.active ? 'page' : undefined} aria-haspopup="dialog" className={className}>
        {inner}
      </button>
    );
  }
  return (
    <Link to={item.to} aria-current={item.active ? 'page' : undefined} aria-disabled={item.disabled || undefined} onClick={(e) => item.disabled && e.preventDefault()} className={className}>
      {inner}
    </Link>
  );
}
